import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import DecoCorners from "./DecoCorners";

const SWF_URL =
  "https://customer-assets.emergentagent.com/job_trance-bookings/artifacts/3v8gyay5_cleo_header.swf";

// Home page hero: the legacy Flash banner takes pride of place, with
// minimal copy + CTAs below. Ruffle (loaded via index.html) auto-polyfills
// the <embed> tag into a working WASM Flash player.
export default function RuffleHero() {
  const sectionRef = useRef(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    let intervalId = null;
    let timeoutId = null;

    const ready = () => { if (!cancelled) setStatus("playing"); };
    const fail = () => { if (!cancelled) setStatus("error"); };

    if (window.RufflePlayer) {
      timeoutId = setTimeout(ready, 800);
    } else {
      intervalId = setInterval(() => {
        if (window.RufflePlayer) {
          clearInterval(intervalId);
          setTimeout(ready, 800);
        }
      }, 200);
      timeoutId = setTimeout(() => {
        if (intervalId) clearInterval(intervalId);
        if (!window.RufflePlayer && !cancelled) fail();
      }, 15000);
    }
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      data-testid="ruffle-hero"
      className="relative overflow-hidden grain bg-charcoal"
      aria-label="Queen Cleopatra"
    >
      {/* Ambient backdrop */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-50"
        style={{
          background:
            "radial-gradient(ellipse at 30% 30%, rgba(75,31,59,0.55) 0%, rgba(17,19,25,0) 60%), radial-gradient(ellipse at 80% 80%, rgba(201,160,75,0.18) 0%, rgba(17,19,25,0) 55%)",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-10 lg:px-16 pt-10 md:pt-16 pb-10 md:pb-14">
        {/* Eyebrow */}
        <div className="eyebrow fade-up mb-5 md:mb-7 flex items-center">
          <span className="inline-block w-8 h-px bg-gold align-middle mr-3" />
          Queen Cleopatra · Erotic Hypnotist
        </div>

        {/* The banner itself - gold-line frame */}
        <figure className="relative fade-up delay-1">
          <div className="absolute inset-0 -translate-x-2 -translate-y-2 border border-[#c9a04b]/35 pointer-events-none" />
          <div className="absolute inset-0 translate-x-2 translate-y-2 border border-[#c9a04b]/15 pointer-events-none" />
          <DecoCorners size={48} opacity={0.9} inset={-11} testid="hero-ruffle-deco" />
          <div className="relative w-full border border-[#c9a04b]/40 bg-charcoal">
            <div className="w-full aspect-[10/3] sm:aspect-[12/3] md:aspect-[16/3] bg-charcoal flex items-center justify-center">
              <embed
                type="application/x-shockwave-flash"
                src={SWF_URL}
                data-testid="ruffle-embed-hero"
                style={{
                  width: "100%",
                  height: "100%",
                  display: status === "error" ? "none" : "block",
                  backgroundColor: "#111319",
                }}
                data-autoplay="on"
                data-letterbox="on"
                data-quality="high"
              />
              {status === "loading" && (
                <div className="absolute inset-0 flex items-center justify-center text-cream/40 text-xs uppercase tracking-[0.22em] pointer-events-none bg-charcoal/60">
                  <span className="inline-block w-2 h-2 bg-gold mr-3 animate-pulse" />
                  Awakening the spiral…
                </div>
              )}
              {status === "error" && (
                <div className="w-full px-6 py-10 text-center text-cream/60">
                  <div className="eyebrow mb-2 text-cream/40">Banner unavailable</div>
                  <div className="text-sm">The legacy banner could not load in this browser.</div>
                </div>
              )}
            </div>
          </div>
          <figcaption className="sr-only">Animated banner - Queen Cleopatra, professional erotic hypnotist</figcaption>
        </figure>

        {/* CTAs + tagline */}
        <div className="mt-10 md:mt-14 grid md:grid-cols-12 gap-8 md:gap-12 items-center fade-up delay-2">
          <div className="md:col-span-7">
            <p className="font-serif text-2xl md:text-3xl text-cream/90 leading-snug">
              In-person erotic hypnosis and professionally produced,
              <em className="text-gold"> fly-on-the-wall</em> session videos -
              discreet, consensual, unforgettable.
            </p>
          </div>
          <div className="md:col-span-5 flex flex-col sm:flex-row md:justify-end gap-3">
            <Link to="/book" className="btn-primary" data-testid="hero-book-cta">
              Book a Session <ArrowRight size={14} className="ml-2" />
            </Link>
            <Link to="/#newsletter" className="btn-secondary" data-testid="hero-mp3-cta">
              Hear "My Pleasure"
            </Link>
          </div>
        </div>

        {/* Trust badges */}
        <div className="mt-10 md:mt-12 flex flex-wrap items-center gap-6 md:gap-8 text-xs uppercase tracking-[0.22em] text-cream/45 fade-up delay-3">
          <span>Discreet</span>
          <span className="text-gold">·</span>
          <span>Consensual</span>
          <span className="text-gold">·</span>
          <span>British Erotic Hypnotist · London</span>
          <span className="text-gold">·</span>
          <span>Adults 18+</span>
        </div>
      </div>

      <div className="relative z-10 gold-divider max-w-7xl mx-auto" />
    </section>
  );
}
